import { useState, useCallback } from 'react';
import { message } from 'antd';
import apiClient from 'services/apiClient';
import { MAX_SELECTED_MEDIA } from './mediaKindUtils';

/**
 * Manages the list of media items queued for a chat send action.
 *
 * Handles library picks, device file uploads (with optimistic placeholders),
 * upload completion/failure, removal, and discard of server-side temp media.
 *
 * @param {object} params
 * @param {import('rc-field-form').FormInstance} params.form  - Ant Design form instance (reads newMsg for send-disable logic)
 * @param {string|number} params.id                          - Active contact ID (used when discarding device media)
 * @param {object|undefined} params.chatAccount              - Account object from LiveChatContext
 * @param {object|undefined} params.UserData                 - User data from MyContext
 */
const useSelectedMedia = ({ form, id, chatAccount, UserData }) => {
  const [selectedMedia, setSelectedMedia] = useState([]);
  const [disable, setDisable] = useState(true);

  // ─── Internal helpers ──────────────────────────────────────────────────────

  const revokePreviewUrl = useCallback((item) => {
    if (item?.previewUrl) {
      URL.revokeObjectURL(item.previewUrl);
    }
  }, []);

  /**
   * Recomputes whether the send button should be disabled based on the
   * current media list and the message text field.
   *
   * @param {Array}            items           - Current selectedMedia array
   * @param {boolean|undefined} hasTextOverride - Pass true/false to skip reading the form field
   */
  const updateSendDisabled = useCallback((items, hasTextOverride) => {
    const hasText =
      hasTextOverride !== undefined
        ? hasTextOverride
        : Boolean(form.getFieldValue('newMsg')?.trim());
    const hasUploading = items.some((item) => item.status === 'uploading');
    const hasReadyMedia = items.some(
      (item) => item.status !== 'uploading' && item.id
    );
    setDisable(hasUploading || (!hasText && !hasReadyMedia));
  }, [form]);

  // ─── Public API ────────────────────────────────────────────────────────────

  /**
   * Adds a media item sourced from the media library.
   * Returns true if it was added, false if capped or duplicate.
   */
  const addSelectedMedia = useCallback((mediaId, name, link) => {
    if (!mediaId) {
      return false;
    }

    let added = false;
    setSelectedMedia((prev) => {
      if (prev.length >= MAX_SELECTED_MEDIA) {
        message.warning(`Maximum ${MAX_SELECTED_MEDIA} files allowed`);
        return prev;
      }

      if (prev.some((item) => String(item.id) === String(mediaId))) {
        message.info('This file is already added');
        return prev;
      }

      added = true;
      const next = [
        ...prev,
        {
          clientId: `${mediaId}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          id: mediaId,
          name,
          link,
          status: 'ready',
          source: 'library',
        },
      ];
      updateSendDisabled(next);
      return next;
    });

    return added;
  }, [updateSendDisabled]);

  /**
   * Asks the server to delete a temporary device-upload media file.
   * No-op for library items or blob-URL previews.
   */
  const discardChatDeviceMedia = useCallback((item) => {
    if (
      item?.source !== 'device' ||
      item?.status !== 'ready' ||
      !item?.id ||
      !item?.link ||
      String(item.link).startsWith('blob:')
    ) {
      return;
    }

    const parentNumber = chatAccount?.whatsapp_number ?? UserData?.whatsapp_number;

    apiClient
      .post('discard-chat-device-media', {
        media_id: String(item.id),
        media_url: String(item.link),
        parent_number: String(parentNumber ?? ''),
        child_number: String(id ?? ''),
      })
      .catch((error) => {
        console.warn('Failed to discard chat device media', error);
      });
  }, [UserData, chatAccount?.whatsapp_number, id]);

  /**
   * Queues device files as uploading placeholders and returns
   * [{ file, clientId }] entries for the caller to actually upload.
   *
   * @param {File[]} files
   * @param {number} [currentCount=0] - How many items are already queued
   * @returns {{ file: File, clientId: string }[]}
   */
  const beginDeviceMediaUploads = useCallback((files, currentCount = 0) => {
    const pendingEntries = [];
    const newItems = [];

    for (const file of files) {
      if (currentCount + newItems.length >= MAX_SELECTED_MEDIA) {
        break;
      }

      const clientId = `pending-${Date.now()}-${newItems.length}-${Math.random().toString(36).slice(2, 7)}`;
      const previewUrl = URL.createObjectURL(file);

      newItems.push({
        clientId,
        id: null,
        name: file.name,
        link: previewUrl,
        previewUrl,
        status: 'uploading',
        source: 'device',
      });
      pendingEntries.push({ file, clientId });
    }

    if (!newItems.length) {
      return [];
    }

    setSelectedMedia((prev) => {
      const next = [...prev, ...newItems];
      updateSendDisabled(next);
      return next;
    });

    return pendingEntries;
  }, [updateSendDisabled]);

  /** Marks a device upload as ready once the server returns the media ID/URL. */
  const completeDeviceMediaUpload = useCallback((clientId, mediaId, name, link) => {
    setSelectedMedia((prev) => {
      const next = prev.map((item) => {
        if (item.clientId !== clientId) {
          return item;
        }
        revokePreviewUrl(item);
        return {
          ...item,
          id: mediaId,
          name: name || item.name,
          link,
          previewUrl: undefined,
          status: 'ready',
          source: 'device',
        };
      });
      updateSendDisabled(next);
      return next;
    });
  }, [revokePreviewUrl, updateSendDisabled]);

  /** Removes a device upload that failed — revokes its blob URL. */
  const failDeviceMediaUpload = useCallback((clientId) => {
    setSelectedMedia((prev) => {
      const failedItem = prev.find((item) => item.clientId === clientId);
      revokePreviewUrl(failedItem);
      const next = prev.filter((item) => item.clientId !== clientId);
      updateSendDisabled(next);
      return next;
    });
  }, [revokePreviewUrl, updateSendDisabled]);

  /** Removes one item by clientId; discards server-side media and revokes blob URLs. */
  const removeSelectedMedia = useCallback((clientId) => {
    let removedItem = null;

    setSelectedMedia((prev) => {
      removedItem = prev.find((item) => item.clientId === clientId) || null;
      const next = prev.filter((item) => item.clientId !== clientId);
      updateSendDisabled(next);
      return next;
    });

    if (removedItem) {
      discardChatDeviceMedia(removedItem);
      revokePreviewUrl(removedItem);
    }
  }, [discardChatDeviceMedia, revokePreviewUrl, updateSendDisabled]);

  /**
   * Clears all selected media.
   *
   * @param {object}  [options]
   * @param {boolean} [options.discardDevice=false] - When true, also asks the server
   *                                                  to delete any ready device-upload files.
   */
  const clearSelectedMedia = useCallback(({ discardDevice = false } = {}) => {
    let itemsToDiscard = [];

    setSelectedMedia((prev) => {
      if (discardDevice) {
        itemsToDiscard = prev.filter(
          (item) =>
            item.source === 'device' &&
            item.status === 'ready' &&
            item.id &&
            item.link
        );
      }
      prev.forEach(revokePreviewUrl);
      return [];
    });

    if (discardDevice) {
      itemsToDiscard.forEach(discardChatDeviceMedia);
    }

    setDisable(true);
  }, [discardChatDeviceMedia, revokePreviewUrl]);

  return {
    selectedMedia,
    disable,
    setDisable,
    updateSendDisabled,
    addSelectedMedia,
    discardChatDeviceMedia,
    beginDeviceMediaUploads,
    completeDeviceMediaUpload,
    failDeviceMediaUpload,
    removeSelectedMedia,
    clearSelectedMedia,
  };
};

export default useSelectedMedia;
